import { Settings } from "lucide-react";
import { NavigationItem } from "../../types";
import { BuilderContext } from "../context";
import { hasFeatureAccess, SENSITIVE_FEATURES } from "@/lib/constants/sensitive-features";

/**
 * Admin — regrouped into nested submenus (People, Finance, System, Maps) so the
 * top-level dropdown stays short instead of listing ~17 flat entries. Each group
 * is itself a NavigationItem with children; the desktop renderer opens the group
 * as a flyout on hover/focus (see DesktopDropdownChild) and the mobile renderer
 * nests it. Group `href` points at the first VISIBLE child so clicking the group
 * never lands on a feature-flag-gated (403) page.
 */
export function buildAdminSection(ctx: BuilderContext): NavigationItem | null {
  const { isAdmin, isClientUser, featureFlags } = ctx;

  // Hide for client users even if flags are misconfigured
  if (!isAdmin || isClientUser) return null;

  const topLevel: NavigationItem[] = [];

  // --- Overview leaves (kept top-level for one-click access) ---
  if (featureFlags.showAdminMenu) {
    topLevel.push({
      id: "admin-dashboard",
      name: "Dashboard",
      href: "/admin",
    });

    // Marketing dashboard — tabbed /mktg overview (Owner Dashboard lives in
    // the Dashboard section; owner-dashboard nav is intentionally not here).
    topLevel.push({
      id: "marketing-dashboard",
      name: "Marketing",
      href: "/mktg",
    });

    // Webinar manager — the ONLY editor for `public.webinars`, which is what
    // the public webinars funnel serves (see WEBINAR_PROGRAM). Sits beside
    // Marketing rather than under System because scheduling a webinar is
    // marketing work, and it had no nav entry at all until now (the page was
    // reachable only by typing /admin/webinars).
    topLevel.push({
      id: "admin-webinars",
      name: "Webinars",
      href: "/admin/webinars",
    });
  }

  // --- People & access ---
  const peopleChildren: NavigationItem[] = [];
  if (featureFlags.canViewUsers) {
    peopleChildren.push({ id: "admin-users", name: "Users", href: "/admin/users" });
  }
  // Employees — people management (moved out of Insights). Preserve its
  // original Insights gate (admin + canViewReports) so access is unchanged.
  if (featureFlags.canViewReports) {
    peopleChildren.push({ id: "employees", name: "Employees", href: "/employees" });
  }

  // --- Finance & billing (all admin-only, no additional flag) ---
  // Plaid financial-data entries are further gated by the per-user "plaid"
  // sensitive-feature grant — hidden for admins who lack it (default-deny).
  // See engineering rule SENSITIVE_FEATURE_ACCESS.
  const canViewPlaid = hasFeatureAccess(
    { sensitive_feature_access: ctx.sensitiveFeatureAccess },
    SENSITIVE_FEATURES.PLAID,
  );
  const plaidChildren: NavigationItem[] = canViewPlaid
    ? [
        { id: "admin-spend-ledger", name: "Spend Ledger", href: "/reports/spend" },
        { id: "admin-plaid-accounts", name: "Bank Accounts", href: "/admin/plaid" },
        { id: "admin-plaid-attribution", name: "Spend Attribution", href: "/admin/plaid-attribution" },
        { id: "admin-plaid-roi", name: "Spend Intelligence", href: "/admin/plaid-roi" },
      ]
    : [];
  const financeChildren: NavigationItem[] = [
    { id: "admin-qb-customers", name: "QuickBooks Customers", href: "/admin/quickbooks/customers" },
    ...plaidChildren,
    { id: "recurring-transactions", name: "Recurring Transactions", href: "/recurring-transactions" },
    { id: "recurring-charges", name: "Recurring Charges", href: "/recurring-charges" },
  ];

  // --- System configuration ---
  const systemChildren: NavigationItem[] = [];
  if (featureFlags.canManageSystemSettings) {
    systemChildren.push({ id: "admin-settings", name: "Settings", href: "/admin/settings" });
  }
  if (featureFlags.canManageAutomation) {
    systemChildren.push({ id: "admin-automation", name: "Automation", href: "/admin/automation" });
  }
  if (featureFlags.canManageData) {
    systemChildren.push({ id: "admin-data", name: "Data Management", href: "/admin/data" });
  }
  if (featureFlags.showAdminMenu) {
    // Communications overview (Admins only)
    systemChildren.push({
      id: "admin-communications",
      name: "Communications",
      href: "/admin/communications",
    });
  }
  systemChildren.push(
    { id: "admin-ticket-templates", name: "Ticket Templates", href: "/admin/tickets/templates" },
    { id: "admin-secure-links", name: "Secure Links", href: "/admin/secure-links" },
  );

  // --- Map utilities (folded in from the retired "Other" group) ---
  const mapChildren: NavigationItem[] = [
    { id: "admin-address-map", name: "Address Map", href: "/admin/address-map" },
    { id: "relationship-map", name: "Relationship Map", href: "/relationship-map" },
  ];

  // Attach each group only if it has visible children; parent href = first child
  // so clicking the group never targets a gated page.
  const groups: Array<{ id: string; name: string; children: NavigationItem[] }> = [
    { id: "admin-people", name: "People", children: peopleChildren },
    { id: "admin-finance", name: "Finance", children: financeChildren },
    { id: "admin-system", name: "System", children: systemChildren },
    { id: "admin-maps", name: "Maps", children: mapChildren },
  ];
  for (const group of groups) {
    if (group.children.length === 0) continue;
    topLevel.push({
      id: group.id,
      name: group.name,
      href: group.children[0].href,
      children: group.children,
    });
  }

  if (topLevel.length === 0) return null;

  return {
    id: "admin",
    name: "Admin",
    href: "/admin",
    icon: Settings,
    visible: true,
    order: 6,
    children: topLevel,
  };
}
